import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Calendar } from 'lucide-react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import axios from 'axios';
import { toast } from 'sonner';
import { useLanguage } from '@/context/LanguageContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ServiceDetail() {
  const { id } = useParams();
  const [service, setService] = useState(null);
  const [loading, setLoading] = useState(true);
  const { lang, t, serviceName } = useLanguage();

  useEffect(() => { loadService(); }, [id]);

  const loadService = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/services/${id}`);
      setService(response.data);
    } catch (error) {
      console.error('Error loading service:', error);
      toast.error(t('serviceDetail', 'notFound'));
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#0066b2] border-t-transparent rounded-full animate-spin" data-testid="service-detail-loading" />
      </div>
    );
  }

  if (!service) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="service-detail-not-found">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('serviceDetail', 'notFound')}</h2>
          <Link to="/servizi"><Button>{t('serviceDetail', 'backToServices')}</Button></Link>
        </div>
      </div>
    );
  }

  const desc = service.description_en && lang === 'en' ? service.description_en : service.description;

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <div className="pt-32 pb-16 px-4 sm:px-6 lg:px-8" data-testid="service-detail">
        <div className="max-w-5xl mx-auto">
          <Link to="/servizi" className="inline-flex items-center text-[#0066b2] hover:text-[#005494] mb-8 font-medium" data-testid="back-to-services">
            <ArrowLeft className="w-4 h-4 mr-2" />
            {t('serviceDetail', 'back')}
          </Link>

          <div className="bg-gradient-to-br from-blue-50 to-white rounded-3xl p-8 md:p-12 mb-12 border border-gray-200">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Space Grotesk' }} data-testid="service-title">{serviceName(service.name)}</h1>
            <div className="text-lg text-gray-600 mb-6 whitespace-pre-line leading-relaxed" data-testid="service-description">{desc}</div>
            <Link to="/prenota">
              <Button size="lg" className="bg-[#0066b2] hover:bg-[#005494]" data-testid="book-service-button">
                <Calendar className="w-5 h-5 mr-2" />
                {t('serviceDetail', 'bookThis')}
              </Button>
            </Link>
          </div>

          {service.images && service.images.length > 0 && (
            <div className="mb-12" data-testid="service-gallery">
              <h2 className="text-3xl font-bold text-gray-900 mb-6" style={{ fontFamily: 'Space Grotesk' }}>{t('serviceDetail', 'gallery')}</h2>
              <div className="grid md:grid-cols-2 gap-4">
                {service.images.map((img, idx) => (
                  <img key={idx} src={img} alt={`${service.name} ${idx + 1}`} className="w-full h-auto max-h-[500px] object-contain rounded-2xl shadow-lg bg-gray-50" data-testid={`service-image-${idx}`} />
                ))}
              </div>
            </div>
          )}

          {service.faqs && service.faqs.length > 0 && (
            <div data-testid="service-faqs">
              <h2 className="text-3xl font-bold text-gray-900 mb-6" style={{ fontFamily: 'Space Grotesk' }}>{t('serviceDetail', 'faq')}</h2>
              <Accordion type="single" collapsible className="space-y-4">
                {service.faqs.map((faq, idx) => (
                  <AccordionItem key={idx} value={`item-${idx}`} className="bg-white border border-gray-200 rounded-xl px-6" data-testid={`faq-item-${idx}`}>
                    <AccordionTrigger className="text-left font-semibold text-gray-900 hover:text-[#0066b2] py-4">{faq.question}</AccordionTrigger>
                    <AccordionContent className="text-gray-600 pb-4">{faq.answer}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}
